/**
 * Vocabulary used throughout `jobman/forms/`:
 *
 * - A **descriptor** is the data for one form entry — `{ key, widget,
 *   label, default, required, ... }`. Pure data, never JSX. Declared by a
 *   job (in its `jobForm`'s `fields: {...}`) or by a tab (`JobForm`'s
 *   `header`/`footer` props), then flattened by `descriptorsFromJobForm`
 *   (`formHelpers.js`) into the array this file actually operates on.
 * - A **widget** is the component that renders a descriptor —
 *   `NumberWidget`, `TextWidget`, `TypedEntryList`, etc. (see
 *   `./widgets`). A descriptor's own `widget` property names which one
 *   applies; the `WIDGETS` registry (`./widgets/index.js`) looks it up.
 *   The same widget commonly renders many different descriptors —
 *   `NumberWidget` alone handles `nSamples`, `batch_size`, `seed`, and
 *   several others, since they're all "a number with a min/max/default,"
 *   just different numbers.
 * - **"Field"** isn't a third, separate thing — it's the informal,
 *   collective word for "one of the form's entries," used when precision
 *   about data vs. rendering doesn't matter (e.g. a job's own
 *   `fields: {...}` declaration). When precision does matter, say
 *   "descriptor" (the data) or "widget" (the renderer) instead.
 *
 * `FieldList` sits at the join: given a list of descriptors, it looks up
 * each one's widget and renders it. At this point the precision no longer matters,
 * so other components (e.g. `JobForm`) can simply refer to these objects as "fields"
 */
import Box from '@mui/material/Box';
import { WIDGETS } from './widgets';

function resolve(value, formState) {
    return typeof value === 'function' ? value(formState) : value;
}

function renderWidget(descriptor, formState, setFormState) {
    const Widget = WIDGETS[descriptor.widget];
    return (
        <Widget
            key={descriptor.key}
            descriptor={descriptor}
            formState={formState}
            setFormState={setFormState}
        />
    );
}

// Consecutive descriptors each marked `row: true` render together in one
// flex row instead of each stacking on its own line — e.g. a
// job-type picker and a job-name input a tab wants sitting side by side.
// Purely a rendering hint on otherwise ordinary descriptors: it changes
// nothing about how they're declared, found, or resolved, so this is the
// only place that needs to know about it. A run is just "however many
// consecutive descriptors have `row: true`" — there's no name to invent,
// so this only supports one run of grouped descriptors at a time within a
// given descriptor list, which is all anything needs today.
function groupByRow(descriptors) {
    const groups = [];
    for (const descriptor of descriptors) {
        const last = groups[groups.length - 1];
        if (descriptor.row && last?.row) {
            last.descriptors.push(descriptor);
        } else {
            groups.push({ row: !!descriptor.row, descriptors: [descriptor] });
        }
    }
    return groups;
}

/**
 * Renders an ordered array of descriptors against a flat
 * `formState`/`setFormState` pair, skipping any descriptor whose `visible`
 * predicate evaluates false. Has no dependency on React local state vs.
 * Formik — a caller only needs to supply a plain `formState` object and a
 * `setFormState(patch)` function; a caller with nested state (e.g. a
 * sub-object) adapts by scoping that pair to the sub-object.
 *
 * Purely an orchestrator: it doesn't know what any individual widget looks
 * like, only how to lay a list of them out (`groupByRow`) and which one a
 * given descriptor selects (`./widgets`'s `WIDGETS` registry). The actual
 * widget implementations live in `./widgets`, the same way `JobForm.jsx`
 * orchestrates regions without knowing what's inside them.
 * @param {{ descriptors: Array<object>, formState: object,
 *   setFormState: (patch: object) => void }} props
 */
export default function FieldList({ descriptors, formState, setFormState }) {
    const visible = descriptors.filter((descriptor) => (
        descriptor.visible === undefined || resolve(descriptor.visible, formState)
    ));
    return (
        <>
            {groupByRow(visible).map((group) => {
                const widgets = group.descriptors.map(
                    (descriptor) => renderWidget(descriptor, formState, setFormState)
                );
                if (!group.row) return widgets;
                return (
                    <Box key={group.descriptors[0].key} sx={{ display: 'flex', gap: '1.5rem' }}>
                        {widgets}
                    </Box>
                );
            })}
        </>
    );
}
